/**
 * User Domain - Validation Rules
 * Alla valideringsregler för användarhantering
 */

import { validatePhone, validateEmail } from '../../../utils/validation';

/**
 * Validerar lösenord
 * @param {string} password - Lösenordet som ska valideras
 * @param {boolean} isRequired - Om lösenord är obligatoriskt (false vid uppdatering)
 * @returns {boolean} true om lösenordet är giltigt
 */
export const validatePassword = (password, isRequired = true) => {
  if (!password && !isRequired) return true; // Tomt lösenord är okej vid uppdatering
  if (!password && isRequired) return false;
  
  // Tillåt alla tecken (inkl. å ä ö och andra Unicode-bokstäver/symboler).
  // Krav: minst 8 tecken, minst en bokstav (\p{L}) och minst en siffra.
  const passwordRegex = /^(?=.*\p{L})(?=.*\d).{8,}$/u;
  return passwordRegex.test(password);
};

/**
 * Validerar alla fält för användarformulär
 * @param {Object} values - Formulärets värden
 * @param {boolean} isUpdate - Om det är en uppdatering (lösenord är optional)
 * @returns {Object} Objekt med eventuella valideringsfel
 */
export const validateUserForm = (values, isUpdate = false) => {
  const errors = {};
  
  // Validera telefon
  if (values.phone) {
    const phoneValidation = validatePhone(values.phone);
    if (!phoneValidation.isValid) {
      errors.phone = phoneValidation.message || 'Ogiltigt telefonnummer';
    }
  }
  
  // Validera email
  const emailValidation = validateEmail(values.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.message || 'Ogiltig e-postadress';
  }
  
  // Validera lösenord
  if (!isUpdate) {
    // Vid skapande är lösenord obligatoriskt
    if (!validatePassword(values.password, true)) {
      errors.password = 'Lösenordet måste vara minst 8 tecken och innehålla både bokstäver och siffror';
    }
  } else {
    // Vid uppdatering validera endast om lösenord anges
    if (values.password && !validatePassword(values.password, false)) {
      errors.password = 'Lösenordet måste vara minst 8 tecken och innehålla både bokstäver och siffror';
    }
  }
  
  // Validera företagsnamn för UE och projektör
  if ((values.role === 'UE' || values.role === 'projektor') && !values.companyName) {
    errors.companyName = 'Företagsnamn krävs för denna roll';
  }
  
  // Validera leverantör för materialleverantör
  if (values.role === 'materialleverantör' && !values.selectedSupplier) {
    errors.selectedSupplier = 'Du måste välja en leverantör';
  }
  
  // Validera byggföretag för byggare
  if (values.role === 'byggare' && !values.selectedBuilder) {
    errors.selectedBuilder = 'Du måste välja ett byggföretag';
  }
  
  // Validera UE-typ
  if (values.role === 'UE' && !values.contractorType) {
    errors.contractorType = 'Typ av UE krävs';
  }

  // Validera projektör-typ
  if (values.role === 'projektor' && !values.projectorType) {
    errors.projectorType = 'Typ av projektör krävs';
  }

  return errors;
};

/**
 * Hanterar real-time validering för specifika fält
 * @param {string} fieldName - Namnet på fältet
 * @param {string} value - Värdet som ska valideras
 * @returns {Object} { isValid, formattedValue, errorMessage }
 */
export const validateField = (fieldName, value) => {
  switch (fieldName) {
    case 'phone': {
      const validation = validatePhone(value);
      return {
        isValid: validation.isValid,
        formattedValue: validation.formattedValue || value,
        errorMessage: validation.isValid ? '' : validation.message
      };
    }
    
    case 'email': {
      const validation = validateEmail(value);
      return {
        isValid: validation.isValid,
        formattedValue: value,
        errorMessage: validation.isValid ? '' : validation.message
      };
    }
    
    default:
      return {
        isValid: true,
        formattedValue: value,
        errorMessage: ''
      };
  }
};

